/** Data sources and KPIs. Credentials go in once and are never displayed again. */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearch } from 'wouter';
import { api, ApiError, type Me } from '../api.ts';
import { formatValue } from '../components/ClusterPreview.tsx';

type OAuthState = 'not_authorized' | 'ok' | 'expiring' | 'expired';
interface Connection { id: string; type: string; name: string; config: Record<string, unknown>; mock: boolean; hasSecret: boolean; lastOkAt: string | null; lastError: string | null; oauth?: { state: OAuthState; expiresAt: string | null }; webhookUrl?: string; webhookToken?: string }
interface Metric { id: string; name: string; unit: string; connectionId: string; connectionName: string; connectionType: string; refreshSeconds: number; staleAfterMinutes: number | null; source: Record<string, unknown>; latest: { value: number; observedAt: number } | null }

const TYPES = ['bigquery', 'ga4', 'meta', 'google_ads', 'search_console', 'woocommerce', 'shoper', 'stripe', 'youtube', 'postgres', 'rest', 'customer_endpoint', 'webhook'] as const;

export function Connections({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const search = new URLSearchParams(useSearch());
  const oauthResult = search.get('oauth');
  const connections = useQuery({ queryKey: ['connections'], queryFn: () => api<Connection[]>('/api/connections') });
  const metrics = useQuery({ queryKey: ['metrics'], queryFn: () => api<Metric[]>('/api/metrics'), refetchInterval: 30_000 });
  const [results, setResults] = useState<Record<string, { ok: boolean; message: string } | 'busy'>>({});
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<Connection | null>(null);
  const [addingMetric, setAddingMetric] = useState(false);
  const isOwner = me.role === 'owner' && !me.demo;
  const canEdit = me.role !== 'viewer';
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['connections'] }), qc.invalidateQueries({ queryKey: ['metrics'] })]);

  async function test(id: string) {
    setResults((r) => ({ ...r, [id]: 'busy' }));
    const res = await api<{ ok: boolean; message: string }>(`/api/connections/${id}/test`, { method: 'POST' }).catch(() => ({ ok: false, message: t('common.error') }));
    setResults((r) => ({ ...r, [id]: res }));
    await refresh();
  }

  async function authorize(id: string) {
    const { url } = await api<{ url: string }>(`/api/connections/${id}/oauth/start`, { method: 'POST' });
    window.location.assign(url);
  }

  async function remove(kind: 'connections' | 'metrics', id: string) {
    await api(`/api/${kind}/${id}`, { method: 'DELETE' });
    await refresh();
  }

  return (
    <div className="stack">
      <section className="stack" aria-labelledby="ds-title">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h1 id="ds-title">{t('connections.title')}</h1>
          {isOwner && !adding && <button className="btn" onClick={() => (setAdding(true), setCreated(null))}>+ {t('connections.add')}</button>}
        </div>
        <p className="muted">{t('connections.lead')}</p>
        <p className="faint">{t('connections.guide')}: <Link href="/guides/bigquery">BigQuery</Link> · <Link href="/guides/ga4">Google Analytics 4</Link> · <Link href="/guides/meta">Meta</Link> · <Link href="/guides/google_ads">Google Ads</Link> · <Link href="/guides/search_console">Search Console</Link> · <Link href="/guides/woocommerce">WooCommerce</Link> · <Link href="/guides/shoper">Shoper</Link> · <Link href="/guides/stripe">Stripe</Link> · <Link href="/guides/youtube">YouTube</Link></p>
        {me.demo && <p className="card muted">{t('connections.demoNote')}</p>}
        {oauthResult && <p className={oauthResult === 'ok' ? 'ok' : 'error'} role="status">{t(`connections.oauth.result.${oauthResult}`, { defaultValue: oauthResult })}</p>}
        {adding && <AddConnection onCancel={() => setAdding(false)} onCreated={async (c) => (setCreated(c), setAdding(false), await refresh())} />}
        {created?.webhookToken && (
          <div className="card stack" role="status">
            <p className="ok">{t('connections.webhookCreated')}</p>
            <p className="faint">{t('connections.webhookUrl')}</p><code className="copy">{created.webhookUrl}</code>
            <p className="faint">{t('connections.webhookToken')}</p><code className="copy">{created.webhookToken}</code>
            <p className="faint">{t('connections.webhookExample')}</p>
            <code className="copy">{`curl -X POST ${created.webhookUrl} -H "Authorization: Bearer ${created.webhookToken}" -H "Content-Type: application/json" -d '{"values":{"orders_today":57}}'`}</code>
          </div>
        )}
        <ul className="device-list">
          {connections.data?.map((c) => {
            const r = results[c.id];
            return (
              <li key={c.id} className="card stack">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <h2>{c.name}</h2>
                  <span className="badge badge--example">{t(`connections.types.${c.type}`)}{c.mock ? ` · ${t('connections.example')}` : ''}</span>
                </div>
                {c.oauth && (
                  <div className="stack">
                    <span className={`badge badge--${c.oauth.state === 'ok' ? 'online' : c.oauth.state === 'expiring' ? 'stale' : 'offline'}`} style={{ justifySelf: 'start' }}>{t(`connections.oauth.${c.oauth.state}`)}</span>
                    {c.oauth.expiresAt && <p className="faint">{t('connections.oauth.expiresOn', { date: new Date(c.oauth.expiresAt).toLocaleString() })}</p>}
                    {isOwner && c.oauth.state !== 'ok' && <button className="btn btn--small" style={{ justifySelf: 'start' }} onClick={() => authorize(c.id)}>{c.oauth.state === 'not_authorized' ? t('connections.oauth.authorize') : t('connections.oauth.reauthorize')}</button>}
                    {isOwner && c.oauth.state === 'not_authorized' && <p className="faint">{t('connections.oauth.redirect')} <code>{`${location.origin}/api/oauth/${c.type}/callback`}</code></p>}
                  </div>
                )}
                {c.webhookUrl && <p className="faint"><code>{c.webhookUrl}</code></p>}
                {!c.mock && c.type !== 'sample' && (
                  <p className="faint">{t('connections.lastOk')}: {c.lastOkAt ? new Date(c.lastOkAt).toLocaleString() : t('connections.never')}{c.hasSecret ? ` · ${t('connections.secretSet')}` : ''}</p>
                )}
                {c.lastError && <p className="error">{t('connections.lastError')}: {c.lastError}</p>}
                {canEdit && (
                  <div className="row">
                    <button className="btn btn--ghost btn--small" onClick={() => test(c.id)} disabled={r === 'busy'}>{r === 'busy' ? t('connections.testing') : t('connections.test')}</button>
                    {isOwner && <button className="link-btn" onClick={() => remove('connections', c.id)}>{t('connections.delete')}</button>}
                    {r && r !== 'busy' && <span className={r.ok ? 'ok' : 'error'} role="status">{r.message}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="stack" aria-labelledby="kpi-title">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 id="kpi-title">{t('connections.metrics.title')}</h2>
          {canEdit && !addingMetric && <button className="btn btn--ghost" onClick={() => setAddingMetric(true)}>+ {t('connections.metrics.add')}</button>}
        </div>
        <p className="muted">{t('connections.metrics.lead')}</p>
        {addingMetric && connections.data && <AddMetric connections={connections.data.filter((c) => !c.mock && (!me.demo || c.type === 'sample'))} onDone={async () => (setAddingMetric(false), await refresh())} />}
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('connections.metrics.name')}</th><th>{t('connections.metrics.source')}</th><th>{t('connections.metrics.latest')}</th><th /></tr></thead>
            <tbody>
              {metrics.data?.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td className="faint">{m.connectionName}</td>
                  <td>{m.latest ? `${formatValue(m.latest.value)} ${m.unit}` : <span className="faint">{t('connections.metrics.none')}</span>}</td>
                  <td>{canEdit && <button className="link-btn" onClick={() => remove('metrics', m.id)}>{t('connections.metrics.delete')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, children, hint }: { id: string; label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <p className="faint">{hint}</p>}
    </div>
  );
}

function AddConnection({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: Connection) => void }) {
  const { t } = useTranslation();
  const [type, setType] = useState<(typeof TYPES)[number]>('bigquery');
  const [mode, setMode] = useState<'public_stats' | 'owner_analytics'>('public_stats');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const f = (k: string) => `connections.fields.${k}`;

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    let config: Record<string, unknown> = {};
    let secret: string | undefined;
    if (type === 'bigquery') (config = { project: v.project, dataset: v.dataset, table: v.table, ...(v.location ? { location: v.location } : {}) }), (secret = v.secret);
    if (type === 'ga4') (config = { property_id: v.property_id }), (secret = v.secret);
    if (type === 'meta') (config = { ad_account_id: v.ad_account_id, ...(v.pixel_id ? { pixel_id: v.pixel_id } : {}) }), (secret = v.secret);
    if (type === 'google_ads') {
      config = { customer_id: v.customer_id, ...(v.login_customer_id ? { login_customer_id: v.login_customer_id } : {}), oauth_publishing_status: v.publishing };
      secret = JSON.stringify({ developer_token: v.developer_token, client_id: v.client_id, client_secret: v.client_secret });
    }
    if (type === 'search_console') (config = { site_url: v.site_url }), (secret = v.secret);
    if (type === 'woocommerce') (config = { url: v.url }), (secret = JSON.stringify({ key: v.consumer_key, secret: v.consumer_secret }));
    if (type === 'shoper') (config = { url: v.url, timezone: v.timezone || 'Europe/Warsaw' }), (secret = JSON.stringify({ login: v.login, password: v.password }));
    if (type === 'stripe') (config = { currency: v.currency }), (secret = v.secret);
    if (type === 'youtube') {
      config = mode === 'public_stats' ? { mode, channel: v.channel } : { mode, oauth_publishing_status: v.publishing };
      secret = mode === 'public_stats' ? v.secret : JSON.stringify({ client_id: v.client_id, client_secret: v.client_secret });
    }
    if (type === 'postgres') (config = { host: v.host, port: Number(v.port || 5432), database: v.database, user: v.user, ssl: v.ssl === 'on' }), (secret = v.secret);
    if (type === 'rest') (config = { url: v.url, header_name: v.header_name || 'Authorization' }), (secret = v.secret || undefined);
    if (type === 'customer_endpoint') (config = { url: v.url }), (secret = v.secret || undefined);
    setBusy(true);
    setError(null);
    try {
      onCreated(await api<Connection>('/api/connections', { method: 'POST', body: { type, name: v.name, config, ...(secret ? { secret } : {}) } }));
    } catch (err) {
      const details = err instanceof ApiError && Array.isArray(err.details) ? err.details.map((d: { path: string; message: string }) => `${d.path}: ${d.message}`).join(', ') : '';
      setError(`${t('common.error')} ${details}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack narrow" onSubmit={submit} autoComplete="off">
      <Field id="c-type" label={t('connections.type')}>
        <select id="c-type" value={type} onChange={(e) => setType(e.target.value as (typeof TYPES)[number])}>
          {TYPES.map((x) => <option key={x} value={x}>{t(`connections.types.${x}`)}</option>)}
        </select>
      </Field>
      <Field id="c-name" label={t('connections.name')}><input id="c-name" name="name" required maxLength={80} /></Field>
      {type === 'bigquery' && (
        <>
          <div className="grid-2">
            <Field id="c-project" label={t(f('project'))}><input id="c-project" name="project" required /></Field>
            <Field id="c-dataset" label={t(f('dataset'))}><input id="c-dataset" name="dataset" required defaultValue="kpis" /></Field>
            <Field id="c-table" label={t(f('table'))}><input id="c-table" name="table" required defaultValue="kpi_latest" /></Field>
            <Field id="c-loc" label={t(f('location'))}><input id="c-loc" name="location" placeholder="EU" /></Field>
          </div>
          <Field id="c-secret" label={t(f('serviceAccount'))} hint={t('connections.secretHint')}><textarea id="c-secret" name="secret" required rows={5} className="mono-input" /></Field>
        </>
      )}
      {type === 'ga4' && (
        <>
          <Field id="c-property" label={t(f('propertyId'))} hint={t(f('propertyIdHint'))}><input id="c-property" name="property_id" required inputMode="numeric" pattern="[0-9]+" /></Field>
          <Field id="c-secret" label={t(f('serviceAccount'))} hint={t('connections.secretHint')}><textarea id="c-secret" name="secret" required rows={5} className="mono-input" /></Field>
          <Link href="/guides/ga4" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'meta' && (
        <>
          <div className="grid-2">
            <Field id="c-adacc" label={t(f('adAccountId'))} hint={t(f('adAccountIdHint'))}><input id="c-adacc" name="ad_account_id" required pattern="(act_)?[0-9]+" /></Field>
            <Field id="c-pixel" label={t(f('pixelId'))} hint={t(f('pixelIdHint'))}><input id="c-pixel" name="pixel_id" inputMode="numeric" pattern="[0-9]+" /></Field>
          </div>
          <Field id="c-secret" label={t(f('metaToken'))} hint={t('connections.secretHint')}><input id="c-secret" name="secret" type="password" required /></Field>
          <Link href="/guides/meta" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'google_ads' && (
        <>
          <div className="grid-2">
            <Field id="c-cust" label={t(f('customerId'))} hint={t(f('customerIdHint'))}><input id="c-cust" name="customer_id" required pattern="[0-9 \-]+" placeholder="123-456-7890" /></Field>
            <Field id="c-login" label={t(f('loginCustomerId'))} hint={t(f('loginCustomerIdHint'))}><input id="c-login" name="login_customer_id" pattern="[0-9 \-]+" /></Field>
          </div>
          <Field id="c-devtoken" label={t(f('developerToken'))} hint={t('connections.secretHint')}><input id="c-devtoken" name="developer_token" type="password" required /></Field>
          <Field id="c-pub" label={t(f('publishing'))}>
            <select id="c-pub" name="publishing" defaultValue="testing"><option value="testing">{t(f('testing'))}</option><option value="production">{t(f('production'))}</option></select>
          </Field>
          <Field id="c-cid" label={t(f('clientId'))}><input id="c-cid" name="client_id" required /></Field>
          <Field id="c-csec" label={t(f('clientSecret'))} hint={t('connections.secretHint')}><input id="c-csec" name="client_secret" type="password" required /></Field>
          <Link href="/guides/google_ads" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'search_console' && (
        <>
          <Field id="c-site" label={t(f('siteUrl'))} hint={t(f('siteUrlHint'))}><input id="c-site" name="site_url" required placeholder="sc-domain:example.com" /></Field>
          <Field id="c-secret" label={t(f('serviceAccount'))} hint={t('connections.secretHint')}><textarea id="c-secret" name="secret" required rows={5} className="mono-input" /></Field>
          <Link href="/guides/search_console" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'woocommerce' && (
        <>
          <Field id="c-url" label={t(f('shopUrl'))}><input id="c-url" name="url" type="url" required placeholder="https://" /></Field>
          <Field id="c-ck" label={t(f('consumerKey'))}><input id="c-ck" name="consumer_key" required placeholder="ck_…" /></Field>
          <Field id="c-cs" label={t(f('consumerSecret'))} hint={t('connections.secretHint')}><input id="c-cs" name="consumer_secret" type="password" required placeholder="cs_…" /></Field>
          <Link href="/guides/woocommerce" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'shoper' && (
        <>
          <Field id="c-url" label={t(f('shopUrl'))}><input id="c-url" name="url" type="url" required placeholder="https://" /></Field>
          <Field id="c-tz" label={t(f('timezone'))}><input id="c-tz" name="timezone" defaultValue="Europe/Warsaw" /></Field>
          <Field id="c-login" label={t(f('webapiLogin'))}><input id="c-login" name="login" required autoComplete="off" /></Field>
          <Field id="c-pass" label={t(f('webapiPassword'))} hint={t('connections.secretHint')}><input id="c-pass" name="password" type="password" required autoComplete="new-password" /></Field>
          <Link href="/guides/shoper" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'stripe' && (
        <>
          <Field id="c-cur" label={t(f('currency'))} hint={t(f('currencyHint'))}><input id="c-cur" name="currency" required pattern="[A-Za-z]{3}" maxLength={3} placeholder="PLN" /></Field>
          <Field id="c-secret" label={t(f('stripeKey'))} hint={t('connections.secretHint')}><input id="c-secret" name="secret" type="password" required placeholder="rk_live_…" /></Field>
          <Link href="/guides/stripe" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'youtube' && (
        <>
          <Field id="c-mode" label={t(f('mode'))}>
            <select id="c-mode" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="public_stats">{t(f('modePublic'))}</option><option value="owner_analytics">{t(f('modeOwner'))}</option>
            </select>
          </Field>
          {mode === 'public_stats' ? (
            <>
              <Field id="c-channel" label={t(f('channel'))}><input id="c-channel" name="channel" required /></Field>
              <Field id="c-secret" label={t(f('apiKey'))} hint={t('connections.secretHint')}><input id="c-secret" name="secret" type="password" required /></Field>
            </>
          ) : (
            <>
              <Field id="c-pub" label={t(f('publishing'))}>
                <select id="c-pub" name="publishing" defaultValue="testing"><option value="testing">{t(f('testing'))}</option><option value="production">{t(f('production'))}</option></select>
              </Field>
              <Field id="c-cid" label={t(f('clientId'))}><input id="c-cid" name="client_id" required /></Field>
              <Field id="c-csec" label={t(f('clientSecret'))} hint={t('connections.secretHint')}><input id="c-csec" name="client_secret" type="password" required /></Field>
            </>
          )}
          <Link href="/guides/youtube" className="faint">{t('connections.guide')} →</Link>
        </>
      )}
      {type === 'postgres' && (
        <>
          <div className="grid-2">
            <Field id="c-host" label={t(f('host'))}><input id="c-host" name="host" required /></Field>
            <Field id="c-port" label={t(f('port'))}><input id="c-port" name="port" type="number" defaultValue={5432} /></Field>
            <Field id="c-db" label={t(f('database'))}><input id="c-db" name="database" required /></Field>
            <Field id="c-user" label={t(f('user'))}><input id="c-user" name="user" required /></Field>
          </div>
          <label className="checkbox"><input type="checkbox" name="ssl" defaultChecked /> {t(f('ssl'))}</label>
          <Field id="c-secret" label={t(f('password'))} hint={t('connections.secretHint')}><input id="c-secret" name="secret" type="password" required /></Field>
        </>
      )}
      {(type === 'rest' || type === 'customer_endpoint') && (
        <>
          <Field id="c-url" label={t(f('url'))}><input id="c-url" name="url" type="url" required placeholder="https://" /></Field>
          {type === 'rest' && <Field id="c-hn" label={t(f('headerName'))}><input id="c-hn" name="header_name" defaultValue="Authorization" /></Field>}
          <Field id="c-secret" label={t(type === 'rest' ? f('headerValue') : f('endpointToken'))} hint={t('connections.secretHint')}><input id="c-secret" name="secret" type="password" /></Field>
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row">
        <button className="btn" disabled={busy}>{t('connections.create')}</button>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>{t('connections.cancel')}</button>
      </div>
    </form>
  );
}

const YT_PUBLIC = ['subscriberCount', 'viewCount', 'videoCount'];
const YT_OWNER = ['views_28d', 'watch_minutes_28d', 'subscribers_net_28d'];
/** Suggestions only: any GA4 metric API name can be typed. */
const GA4_METRICS = ['activeUsers', 'newUsers', 'sessions', 'engagedSessions', 'screenPageViews', 'eventCount', 'keyEvents', 'totalRevenue', 'purchaseRevenue', 'transactions', 'ecommercePurchases', 'averagePurchaseRevenue', 'sessionKeyEventRate', 'engagementRate', 'advertiserAdCost', 'returnOnAdSpend'];
const GA4_RANGES = ['realtime', 'today', 'yesterday', '7d', '28d', 'month_to_date'];
/** Suggestions only: actions:<action_type>, action_values:<action_type> and pixel:<EventName> can be typed. */
const META_METRICS = ['spend', 'impressions', 'clicks', 'reach', 'frequency', 'ctr', 'cpc', 'cpm', 'inline_link_clicks', 'purchases', 'purchase_value', 'roas', 'actions:lead', 'actions:add_to_cart', 'pixel_events', 'pixel:PageView', 'pixel:AddToCart', 'pixel:InitiateCheckout', 'pixel:Purchase', 'pixel:Lead'];
const META_AD_RANGES = ['today', 'yesterday', '7d', '28d', 'month_to_date'];
const META_PIXEL_RANGES = ['last_hour', '24h'];
const ADS_METRICS = ['cost', 'impressions', 'clicks', 'conversions', 'conversions_value', 'roas', 'ctr', 'average_cpc', 'cost_per_conversion'];
const ADS_RANGES = ['today', 'yesterday', '7d', '30d', 'month_to_date'];
/** Sources whose KPI is a metric from a fixed list plus a period: [metrics, periods, translation key]. */
const LISTED: Record<string, { metrics: string[]; ranges: string[]; key: string }> = {
  google_ads: { metrics: ADS_METRICS, ranges: ADS_RANGES, key: 'ads' },
  search_console: { metrics: ['clicks', 'impressions', 'ctr', 'position'], ranges: ['28d', '7d', '90d'], key: 'gsc' },
  woocommerce: { metrics: ['total_sales', 'net_sales', 'total_orders', 'total_items', 'total_customers', 'average_sales'], ranges: ['today', 'week', 'month', 'last_month', 'year'], key: 'woo' },
  shoper: { metrics: ['total_sales', 'total_orders', 'average_order'], ranges: ['today', '24h', '7d', '30d'], key: 'shoper' },
  stripe: { metrics: ['gross_volume', 'payments', 'average_payment', 'refunded', 'balance_available', 'balance_pending'], ranges: ['today', '24h', '7d'], key: 'stripe' },
};
const SAMPLE = ['roas', 'revenue_today', 'orders_per_hour', 'visitors_now', 'conversion_rate'];

function AddMetric({ connections, onDone }: { connections: Connection[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState(connections[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  /** Meta only: the periods on offer depend on the metric typed (pixel events vs ad metrics). */
  const [metaMetric, setMetaMetric] = useState('');
  const conn = connections.find((c) => c.id === connectionId);
  useEffect(() => (setError(null), setMetaMetric('')), [connectionId]);
  const listed = conn ? LISTED[conn.type] : undefined;
  const ranges = conn?.type === 'ga4' ? { key: 'ga4', list: GA4_RANGES } : listed ? { key: listed.key, list: listed.ranges } : conn?.type === 'meta' ? { key: 'meta', list: /^pixel[_:]/.test(metaMetric) ? META_PIXEL_RANGES : META_AD_RANGES } : null;
  const sf = (k: string) => t(`connections.metrics.sourceFields.${k}`);

  const sourceField = (() => {
    switch (conn?.type) {
      case 'bigquery': case 'customer_endpoint': return { key: 'kpi_name', input: <input id="m-src" name="src" required /> };
      case 'ga4': return {
        key: 'ga4_metric',
        input: (
          <>
            <input id="m-src" name="src" required list="ga4-metrics" pattern="[A-Za-z0-9_:]+" />
            <datalist id="ga4-metrics">{GA4_METRICS.map((m) => <option key={m} value={m} />)}</datalist>
          </>
        ),
      };
      case 'meta': return {
        key: 'meta_metric',
        input: (
          <>
            <input id="m-src" name="src" required list="meta-metrics" pattern="[A-Za-z0-9_.:]+" value={metaMetric} onChange={(e) => setMetaMetric(e.target.value)} />
            <datalist id="meta-metrics">{META_METRICS.map((m) => <option key={m} value={m} />)}</datalist>
          </>
        ),
      };
      case 'google_ads': case 'search_console': case 'woocommerce': case 'shoper': case 'stripe': return { key: 'metric', input: <select id="m-src" name="src">{listed!.metrics.map((m) => <option key={m}>{m}</option>)}</select> };
      case 'youtube': return { key: 'metric', input: <select id="m-src" name="src">{(conn.config.mode === 'owner_analytics' ? YT_OWNER : YT_PUBLIC).map((m) => <option key={m}>{m}</option>)}</select> };
      case 'postgres': return { key: 'sql', input: <textarea id="m-src" name="src" rows={3} required className="mono-input" placeholder="select sum(total) as value from orders where created_at::date = current_date" /> };
      case 'rest': return { key: 'json_path', input: <input id="m-src" name="src" required /> };
      case 'webhook': return { key: 'key', input: <input id="m-src" name="src" required pattern="[A-Za-z0-9_.-]+" /> };
      case 'sample': return { key: 'kpi', input: <select id="m-src" name="src">{SAMPLE.map((m) => <option key={m}>{m}</option>)}</select> };
      default: return null;
    }
  })();

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    try {
      await api('/api/metrics', {
        method: 'POST',
        body: { connectionId, name: v.name, unit: v.unit ?? '', source: ranges ? { metric: v.src, range: v.range } : { [sourceField!.key]: v.src }, refreshSeconds: Number(v.refresh || 300), staleAfterMinutes: v.stale ? Number(v.stale) : null },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? `${t('common.error')} (${err.code})` : t('common.error'));
    }
  }

  return (
    <form className="card stack narrow" onSubmit={submit}>
      <Field id="m-conn" label={t('connections.metrics.source')}>
        <select id="m-conn" value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
          {connections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field id="m-name" label={t('connections.metrics.name')}><input id="m-name" name="name" required maxLength={80} /></Field>
      {sourceField && <Field id="m-src" label={sf(sourceField.key)}>{sourceField.input}</Field>}
      {ranges && (
        <Field id="m-range" label={sf('ga4_range')} hint={sf(`${ranges.key}_range_hint`)}>
          <select id="m-range" name="range" key={`${ranges.key}:${ranges.list[0]}`} defaultValue={ranges.list[0] === 'realtime' ? 'today' : ranges.list[0]}>{ranges.list.map((r) => <option key={r} value={r}>{t(`connections.metrics.${ranges.key}Ranges.${r}`)}</option>)}</select>
        </Field>
      )}
      <div className="grid-2">
        <Field id="m-unit" label={t('connections.metrics.unit')}><input id="m-unit" name="unit" maxLength={24} /></Field>
        <Field id="m-refresh" label={t('connections.metrics.refresh')}><input id="m-refresh" name="refresh" type="number" min={30} defaultValue={300} /></Field>
      </div>
      <Field id="m-stale" label={t('connections.metrics.staleAfter')}><input id="m-stale" name="stale" type="number" min={1} /></Field>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row">
        <button className="btn">{t('connections.metrics.add')}</button>
        <button type="button" className="btn btn--ghost" onClick={onDone}>{t('connections.cancel')}</button>
      </div>
    </form>
  );
}
